/**
 * lazyPages — All lazy-loaded page components.
 *
 * Extracted from App.tsx to keep the main router file focused on
 * route structure rather than import boilerplate.
 */
import { lazy } from 'react';

// Onboarding
export const OnboardingWizard = lazy(() =>
  import('./shared/components/OnboardingWizard').then((m) => ({ default: m.OnboardingWizard })),
);

// Top-level pages
export const LandingPage = lazy(() =>
  import('./features/landing/LandingPage').then((m) => ({ default: m.LandingPage })),
);
export const TrialPage = lazy(() =>
  import('./features/landing/TrialPage').then((m) => ({ default: m.TrialPage })),
);
export const PlazaPage = lazy(() =>
  import('./features/plaza/PlazaPage').then((m) => ({ default: m.PlazaPage })),
);
export const CreativePanel = lazy(() =>
  import('./features/creative/CreativePanel').then((m) => ({ default: m.CreativePanel })),
);

// Studio
export const StudioLayout = lazy(() =>
  import('./features/studio/StudioLayout').then((m) => ({ default: m.StudioLayout })),
);
export const StudioIndexRedirect = lazy(() =>
  import('./features/studio/StudioLayout').then((m) => ({ default: m.StudioIndexRedirect })),
);
export const SeedPage = lazy(() =>
  import('./features/studio/seed/SeedPage').then((m) => ({ default: m.SeedPage })),
);
export const FoundationPage = lazy(() =>
  import('./features/studio/foundation/FoundationPage').then((m) => ({ default: m.FoundationPage })),
);

// Auth pages
export const LoginPage = lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
export const RegisterPage = lazy(() => import('./features/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
export const VerifyEmailPage = lazy(() => import('./features/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
export const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
export const ResetPasswordPage = lazy(() => import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));

// Plaza sub-pages
export const NovelDetailPage = lazy(() =>
  import('./features/plaza/NovelDetailPage').then((m) => ({ default: m.NovelDetailPage })),
);
export const ReaderPage = lazy(() =>
  import('./features/plaza/ReaderPage').then((m) => ({ default: m.ReaderPage })),
);
export const GenrePage = lazy(() =>
  import('./features/plaza/GenrePage').then((m) => ({ default: m.GenrePage })),
);
export const GenreListPage = lazy(() =>
  import('./features/plaza/GenreListPage').then((m) => ({ default: m.GenreListPage })),
);

// Bookshelf
export const BookshelfPage = lazy(() =>
  import('./features/bookshelf/BookshelfPage').then((m) => ({ default: m.BookshelfPage })),
);

// Heavy studio pages (React Flow ~300kB, TipTap ~200kB)
export const OutlinePage = lazy(() =>
  import('./features/studio/outline/OutlinePage').then((m) => ({ default: m.OutlinePage })),
);
export const WritingPage = lazy(() =>
  import('./features/studio/writing/WritingPage').then((m) => ({ default: m.WritingPage })),
);
export const PublishPage = lazy(() =>
  import('./features/studio/publish/PublishPage').then((m) => ({ default: m.PublishPage })),
);
export const CodexPage = lazy(() =>
  import('./features/studio/codex/CodexPage').then((m) => ({ default: m.CodexPage })),
);
export const ImportExportPage = lazy(() =>
  import('./features/studio/importExport/ImportExportPage').then((m) => ({ default: m.ImportExportPage })),
);
export const NovelSettingsPage = lazy(() =>
  import('./features/studio/settings/NovelSettingsPage').then((m) => ({ default: m.NovelSettingsPage })),
);

// Profile pages
export const ProfileSettingsPage = lazy(() =>
  import('./features/profile/ProfileSettingsPage').then((m) => ({ default: m.ProfileSettingsPage })),
);
export const UserProfilePage = lazy(() =>
  import('./features/profile/UserProfilePage').then((m) => ({ default: m.UserProfilePage })),
);

// Admin pages
export const AdminLayout = lazy(() =>
  import('./features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
export const AdminDashboardPage = lazy(() =>
  import('./features/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
);
export const ReviewQueuePage = lazy(() =>
  import('./features/admin/ReviewQueuePage').then((m) => ({ default: m.ReviewQueuePage })),
);
export const ReviewDetailPage = lazy(() =>
  import('./features/admin/ReviewDetailPage').then((m) => ({ default: m.ReviewDetailPage })),
);
export const ModActionLogPage = lazy(() =>
  import('./features/admin/ModActionLogPage').then((m) => ({ default: m.ModActionLogPage })),
);
export const ReportManagePage = lazy(() =>
  import('./features/admin/ReportManagePage').then((m) => ({ default: m.ReportManagePage })),
);
export const UserManagePage = lazy(() =>
  import('./features/admin/UserManagePage').then((m) => ({ default: m.UserManagePage })),
);
export const CommentAuditPage = lazy(() =>
  import('./features/admin/CommentAuditPage').then((m) => ({ default: m.CommentAuditPage })),
);
export const SystemConfigPage = lazy(() =>
  import('./features/admin/SystemConfigPage').then((m) => ({ default: m.SystemConfigPage })),
);
export const AdminNovelManager = lazy(() =>
  import('./features/admin/AdminNovelManager').then((m) => ({ default: m.AdminNovelManager })),
);
export const UserDetailPage = lazy(() =>
  import('./features/admin/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
export const ArenaAdminPage = lazy(() =>
  import('./features/admin/ArenaAdminPage').then((m) => ({ default: m.ArenaAdminPage })),
);

// Arena pages
export const ArenaHomePage = lazy(() =>
  import('./features/arena/ArenaHomePage').then((m) => ({ default: m.ArenaHomePage })),
);
export const RoundDetailPage = lazy(() =>
  import('./features/arena/RoundDetailPage').then((m) => ({ default: m.RoundDetailPage })),
);
export const SeedDetailPage = lazy(() =>
  import('./features/arena/SeedDetailPage').then((m) => ({ default: m.SeedDetailPage })),
);
export const MyArenaPage = lazy(() =>
  import('./features/arena/MyArenaPage').then((m) => ({ default: m.MyArenaPage })),
);
export const OutlineReviewPage = lazy(() =>
  import('./features/arena/OutlineReviewPage').then((m) => ({ default: m.OutlineReviewPage })),
);

// Derivatives pages
export const DerivativesHomePage = lazy(() =>
  import('./features/derivatives/DerivativesHomePage').then((m) => ({ default: m.DerivativesHomePage })),
);
export const SourceDerivativesPage = lazy(() =>
  import('./features/derivatives/SourceDerivativesPage').then((m) => ({ default: m.SourceDerivativesPage })),
);
export const DerivativeTreePage = lazy(() =>
  import('./features/derivatives/DerivativeTreePage').then((m) => ({ default: m.DerivativeTreePage })),
);

// Community pages
export const CommunityPage = lazy(() =>
  import('./features/community/CommunityPage').then((m) => ({ default: m.CommunityPage })),
);
